import { useMemo, useState } from "react";
import { useNetsuiteBankBalanceAccounts } from "@api/hooks";
import { NETSUITE_ACCOUNT_IDS } from "@utils";
import {
  PreviewField,
  PreviewInput,
  useJournalEntryLineLookups,
  fmtLineAmount,
  computeLineBalance,
} from "@common";

// SFC is the account these reversals almost always draw on, but NetSuite's own
// ordering buries it in the middle of ~180 "Bank Balance" accounts. Pin it to the
// top, the same convention the cash-flow summary and Daily Bank Balance use.
const SFC_ACCOUNT_ID = NETSUITE_ACCOUNT_IDS.RBC_SF_CORPORATION_9411;

/**
 * Move the SFC account to the front, leaving every other account in NetSuite's
 * order. Applied AFTER filtering, so a search that legitimately excludes SFC is
 * left alone rather than having it forced back in — and typing "9411" or "S.F."
 * still just narrows to it.
 */
const pinSfcFirst = (accounts) => {
  const idx = accounts.findIndex((b) => String(b.id) === String(SFC_ACCOUNT_ID));
  if (idx <= 0) return accounts; // absent, or already first — nothing to do
  return [accounts[idx], ...accounts.slice(0, idx), ...accounts.slice(idx + 1)];
};

export default function ReverseJournalEntryPreview({ preview, onChange }) {
  const items = useMemo(() => preview?.payload?.line?.items ?? [], [preview]);

  const bankAccountsQuery = useNetsuiteBankBalanceAccounts();
  const bankAccounts = useMemo(() => {
    const data = bankAccountsQuery.data;
    return data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
  }, [bankAccountsQuery.data]);
  const [bankSearch, setBankSearch] = useState("");
  const filteredBankAccounts = useMemo(() => {
    const q = bankSearch.trim().toLowerCase();
    if (!q) return pinSfcFirst(bankAccounts);
    return pinSfcFirst(
      bankAccounts.filter((b) =>
        String(b.fullname ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [bankAccounts, bankSearch]);

  const [accountSearch, setAccountSearch] = useState("");
  const filteredExtraAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    if (!q) return pinSfcFirst(bankAccounts);
    return pinSfcFirst(
      bankAccounts.filter((b) =>
        String(b.fullname ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [bankAccounts, accountSearch]);

  const [editingExtraIdx, setEditingExtraIdx] = useState(null);

  const { accountNameById, subsidiaryNameById, accountsQuery, subsidiariesQuery } =
    useJournalEntryLineLookups(preview?.payload);

  if (!preview) return null;
  const { payload, amount } = preview;

  const { totalDebit, totalCredit, balanced } = computeLineBalance(items);

  const headerSubName =
    subsidiaryNameById[String(payload?.subsidiary?.id)] ||
    (subsidiariesQuery.isLoading ? "Loading…" : payload?.subsidiary?.id || "—");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-palette-gray-divider bg-palette-gray-soft p-3">
        <PreviewField label="Header Subsidiary" value={headerSubName} />
        <PreviewField label="Transaction Date">
          <PreviewInput
            type="date"
            value={payload?.trandate ?? ""}
            onChange={(e) => onChange?.({ trandate: e.target.value })}
            className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
          />
        </PreviewField>
        <PreviewField label="Amount">
          <PreviewInput
            type="number"
            step="0.01"
            min="0"
            value={amount ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              const next = raw === "" ? null : Number(raw);
              onChange?.({ amount: next });
            }}
            className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
            placeholder="0.00"
          />
        </PreviewField>
        <PreviewField label="Lines" value={items.length} />
        <div className="sm:col-span-2">
          {/* Single-subsidiary entries post as a NORMAL journal entry and always
              describe the same movement (SFC cash into an SFC bank account), so
              their memo is fixed — and the listing finds them by exactly this
              string. Editing it would make the entry unfindable, hence read-only.
              The intercompany memo stays editable. */}
          <PreviewField label="Memo">
            {preview.isSingleSubsidiary ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-palette-gray-text">
                  {payload?.memo ?? "—"}
                </span>
                <span
                  className="inline-flex items-center whitespace-nowrap rounded-full bg-palette-slate-muted px-2 py-[1px] text-[10px] font-semibold text-palette-slate-heading ring-1 ring-inset ring-palette-slate-ring"
                  title="Fixed for single-subsidiary entries — the listing finds them by this memo"
                >
                  Locked
                </span>
              </div>
            ) : (
              <PreviewInput
                type="text"
                value={payload?.memo ?? ""}
                onChange={(e) => onChange?.({ memo: e.target.value })}
                className="w-full text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
              />
            )}
          </PreviewField>
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-palette-gray-heading">
            Line Items ({items.length})
          </span>
          <div className="flex items-center gap-3">
            <span
              className={`text-xs font-semibold ${balanced ? "text-palette-emerald-hover" : "text-palette-red-heading"}`}
            >
              {balanced ? "✓ Balanced" : "✗ Not balanced"}
            </span>
            {!balanced && (
              <span className="text-xs text-palette-red-badge font-semibold">
                Diff: {fmtLineAmount(Math.abs(totalDebit - totalCredit))}
              </span>
            )}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-palette-gray-divider">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-palette-gray-subtle text-palette-gray-heading">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Subsidiary</th>
                <th className="text-left px-3 py-3 font-semibold">Account</th>
                <th className="text-right px-3 py-3 font-semibold">Debit</th>
                <th className="text-right px-3 py-3 font-semibold">Credit</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-palette-gray-secondary">
                    No line items.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const sid = it?.lineSubsidiary?.id;
                  const aid = it?.account?.id;
                  const subName = sid != null ? subsidiaryNameById[String(sid)] : "";
                  const acctName = aid != null ? accountNameById[String(aid)] : "";
                  const isDebit = Number(it.debit) > 0;
                  const isCredit = Number(it.credit) > 0;
                  const rowCls = isDebit
                    ? "bg-palette-emerald-soft hover:bg-palette-emerald-muted"
                    : isCredit
                      ? "bg-palette-rose-soft hover:bg-palette-rose-muted"
                      : "";
                  const isExtra = it.__extra;

                  // Calculate the index of this extra line in the additionalLines array
                  let extraLineIdx = -1;
                  if (isExtra) {
                    let extraCount = 0;
                    for (let i = 0; i < idx; i++) {
                      if (items[i]?.__extra) extraCount++;
                    }
                    extraLineIdx = extraCount;
                  }

                  return (
                    <tr
                      key={idx}
                      className={`border-t border-palette-gray-subtle align-top ${rowCls}`}
                    >
                      <td className="px-3 py-3">
                        {subName ? (
                          <span className="text-palette-gray-text">{subName}</span>
                        ) : subsidiariesQuery.isLoading ? (
                          <span className="text-palette-gray-placeholder italic text-xs">
                            Loading…
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-palette-gray-secondary">
                            {sid ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {it.__bank ? (
                          <div className="flex flex-col gap-1 min-w-[16rem]">
                            {aid ? (
                              <div className="flex items-center justify-between gap-2 rounded border border-palette-emerald-border bg-palette-emerald-soft px-2 py-1">
                                <span className="text-sm text-palette-emerald-text">
                                  {bankAccounts.find((b) => String(b.id) === String(aid))
                                    ?.fullname ?? aid}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    onChange?.({ bankAccountId: "", bankSubsidiaryId: "" });
                                    setBankSearch("");
                                  }}
                                  className="text-xs text-palette-emerald-hover hover:underline"
                                >
                                  Change
                                </button>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="text"
                                  value={bankSearch}
                                  onChange={(e) => setBankSearch(e.target.value)}
                                  placeholder="Search account…"
                                  className="text-sm text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                                />
                                <div className="max-h-40 overflow-y-auto rounded border border-palette-gray-divider bg-white">
                                  {bankAccountsQuery.isLoading ? (
                                    <div className="px-2 py-1 text-xs text-palette-gray-placeholder italic">
                                      Loading bank accounts…
                                    </div>
                                  ) : filteredBankAccounts.length === 0 ? (
                                    <div className="px-2 py-1 text-xs text-palette-gray-placeholder italic">
                                      No matches
                                    </div>
                                  ) : (
                                    filteredBankAccounts.map((b) => (
                                      <button
                                        type="button"
                                        key={b.id}
                                        onClick={() => {
                                          const sub = b?.subsidiary?.id ?? b?.subsidiary ?? "";
                                          onChange?.({
                                            bankAccountId: b.id,
                                            bankSubsidiaryId: sub ? String(sub) : "",
                                          });
                                          setBankSearch("");
                                        }}
                                        className="block w-full text-left px-2 py-1 text-sm text-palette-gray-text hover:bg-palette-indigo-soft"
                                      >
                                        {b.fullname}
                                      </button>
                                    ))
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        ) : isExtra ? (
                          <div className="flex flex-col gap-1 min-w-[16rem]">
                            {(() => {
                              const isEditing = editingExtraIdx === extraLineIdx;
                              const selectedAccount = bankAccounts.find(
                                (b) => String(b.id) === String(aid),
                              );

                              if (aid && !isEditing) {
                                return (
                                  <div className="flex items-center justify-between gap-2 rounded border border-palette-indigo-border bg-palette-indigo-soft px-2 py-1">
                                    <span className="text-sm text-palette-indigo-text">
                                      {selectedAccount?.fullname ?? aid}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingExtraIdx(extraLineIdx);
                                        setAccountSearch("");
                                      }}
                                      className="text-xs text-palette-indigo-heading hover:underline"
                                    >
                                      Change
                                    </button>
                                  </div>
                                );
                              }
                              return (
                                <>
                                  <input
                                    type="text"
                                    value={accountSearch}
                                    onChange={(e) => setAccountSearch(e.target.value)}
                                    placeholder="Search account…"
                                    className="text-sm text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                                  />
                                  <div className="max-h-40 overflow-y-auto rounded border border-palette-gray-divider bg-white">
                                    {bankAccountsQuery.isLoading ? (
                                      <div className="px-2 py-1 text-xs text-palette-gray-placeholder italic">
                                        Loading bank accounts…
                                      </div>
                                    ) : filteredExtraAccounts.length === 0 ? (
                                      <div className="px-2 py-1 text-xs text-palette-gray-placeholder italic">
                                        No matches
                                      </div>
                                    ) : (
                                      filteredExtraAccounts.map((b) => (
                                        <button
                                          type="button"
                                          key={b.id}
                                          onClick={() => {
                                            const sub = b?.subsidiary?.id ?? b?.subsidiary ?? "";
                                            onChange?.({
                                              additionalLines: preview.additionalLines.map(
                                                (l, i) =>
                                                  i === extraLineIdx
                                                    ? {
                                                        ...l,
                                                        accountId: b.id,
                                                        subsidiaryId: sub ? String(sub) : "",
                                                      }
                                                    : l,
                                              ),
                                            });
                                            setEditingExtraIdx(null);
                                            setAccountSearch("");
                                          }}
                                          className="block w-full text-left px-2 py-1 text-sm text-palette-gray-text hover:bg-palette-indigo-soft"
                                        >
                                          {b.fullname}
                                        </button>
                                      ))
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        ) : acctName ? (
                          <span className="text-palette-gray-text">{acctName}</span>
                        ) : accountsQuery.isLoading ? (
                          <span className="text-palette-gray-placeholder italic text-xs">
                            Loading…
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-palette-gray-secondary">
                            {aid ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">
                        {/* The bank line is READ-ONLY and mirrors the Amount above —
                            editing it here could only unbalance the entry against the
                            SFC leg. Change Amount to change both. */}
                        {it.__bank && it.debit != null ? (
                          <span title="Always equals the Amount above — edit Amount to change it">
                            {fmtLineAmount(it.debit)}
                          </span>
                        ) : isExtra && it.debit != null ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={(preview.additionalLines || [])[extraLineIdx]?.amount ?? 0}
                            onChange={(e) => {
                              const val = e.target.value === "" ? 0 : Number(e.target.value);
                              onChange?.({
                                additionalLines: preview.additionalLines.map((l, i) =>
                                  i === extraLineIdx ? { ...l, amount: val } : l,
                                ),
                              });
                            }}
                            className="text-sm font-mono text-right px-2 py-1 rounded border border-palette-gray-border bg-white text-palette-gray-heading focus:outline-none focus:ring-2 focus:ring-palette-indigo-label w-28"
                          />
                        ) : it.debit != null ? (
                          fmtLineAmount(it.debit)
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">
                        <div className="flex items-center justify-end gap-2">
                          <span>{it.credit != null ? fmtLineAmount(it.credit) : ""}</span>
                          {isExtra && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = preview.additionalLines.filter(
                                  (_, i) => i !== extraLineIdx,
                                );
                                onChange?.({ additionalLines: updated });
                              }}
                              className="text-xs text-palette-red-badge hover:text-palette-red-text hover:underline"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="bg-palette-gray-soft">
              <tr className="border-t border-palette-gray-divider">
                <td colSpan={2} className="px-3 py-3 text-right font-semibold">
                  Totals
                </td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(totalDebit)}</td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(totalCredit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-3 flex justify-start">
          <button
            type="button"
            onClick={() => {
              onChange?.({
                additionalLines: [
                  ...(preview.additionalLines || []),
                  { accountId: "", isDebit: true, amount: 0, subsidiaryId: "" },
                ],
              });
            }}
            className="text-xs text-palette-indigo-badge hover:text-palette-indigo-text hover:underline font-medium"
          >
            + Add more
          </button>
        </div>
      </div>

      <p className="text-xs text-palette-gray-secondary">
        Edit the amount above to update all line debits/credits — the selected account&apos;s debit
        always matches it and can&apos;t be edited on its own. Lines added with &ldquo;+ Add
        more&rdquo; carry their own amounts.
      </p>
    </div>
  );
}
